import { ref } from 'vue'
import { useRouter } from 'vue-router'
import store from '@/store'
import { PLANS } from '@/utils/payments'

const PROGRESS_MESSAGES = {
  account: 'Step 1 of 3: Setting up your account…',
  subscription: 'Step 2 of 3: Starting your 2 week free trial…',
  workspace: 'Step 3 of 3: Getting your workspace ready…'
}

export const useStartTrial = () => {
  const router = useRouter()
  const loadingStartTrial = ref(false)

  const startTrial = async ({ users } = {}) => {
    if (loadingStartTrial.value) {
      return
    }

    const currentUser = store.state.user.user
    // The backend turns this list into the subscription rows that grant access, and rejects an
    // empty one, so the owner is always on it even when no teammates were chosen.
    const seats = users?.length
      ? users.map((user) => ({ email: user.email }))
      : [{ email: currentUser?.email }]

    // A seat with no email would buy a subscription nobody can use: the rows keyed by it are what
    // grant access. Reachable if the user record has not loaded yet, so fail before Stripe.
    if (seats.some((seat) => !seat.email)) {
      store.commit('snackbar/showMessage', {
        content: 'Could not start the trial, please try again.',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })

      return
    }

    loadingStartTrial.value = true
    // The blocking overlay is what stops the other plan cards being clicked while Stripe works:
    // a second plan chosen mid-flight would navigate away and land the user on the wrong page.
    store.commit('loading/startLoading', PROGRESS_MESSAGES.account)
    store.commit('loading/setLoadingDimmed', true)

    try {
      const trialPlan = PLANS.find((candidate) => candidate.value === 'pro')
      const response = await store.dispatch('payments/startFreeTrial', {
        subscriptionPriceId: trialPlan.prices.monthly,
        users: seats,
        onProgress: (phase) => store.commit('loading/setLoadingMessage', PROGRESS_MESSAGES[phase])
      })

      if (response?.success) {
        store.commit('loading/setLoadingMessage', PROGRESS_MESSAGES.workspace)
        // Both refreshes have to land before navigating, or the router guard still sees NO_ACCESS
        // and bounces the new subscriber back to /plans.
        await Promise.all([
          store.dispatch('payments/getActiveSubscription'),
          store.dispatch('payments/getPayerUserSubscription')
        ])
        window.trackPlanPurchased({ plan: 'trial' })
        await router.push({ path: '/dashboard', query: { plan: 'trial' } })
      } else {
        store.commit('snackbar/showMessage', {
          content: response?.message || 'Could not start the trial, please try again.',
          timeout: 6000,
          color: 'red',
          location: 'center center'
        }, { root: true })
      }

      return response
    } finally {
      // Without this the overlay outlives any thrown error and locks the whole app.
      // The dimmed style is cleared by App.vue once the overlay has faded out, not here, so it
      // doesn't flash light mid-fade or while the dashboard's own loader keeps the overlay up.
      store.commit('loading/stopLoading')
      loadingStartTrial.value = false
    }
  }

  return { startTrial, loadingStartTrial }
}
